import { useState, useEffect } from 'react';
import { Button, Spin, Empty, List, Avatar, Tooltip, Tag, Modal } from 'antd';
import { ImCancelCircle } from 'react-icons/im';
import { 
  MedicineBoxOutlined, 
  EnvironmentOutlined, 
  DisconnectOutlined, 
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { getMedicationById, removeMedicationFromFacility, findFacilitiesWithMedication } from '../../../services/medications';
import './UnassignMedicationModal.css';

const UnassignMedicationModal = ({ medicationId, onClose, onUnassignComplete }) => {
  const [loading, setLoading] = useState(false);
  const [medication, setMedication] = useState(null);
  const [assignedFacilities, setAssignedFacilities] = useState([]);
  const [unassignLoading, setUnassignLoading] = useState(false);
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);

  // Format address for display
  const formatAddress = (address) => {
    if (!address) return "No address available";
    
    // If address is a string, return it directly
    if (typeof address === 'string') return address;
    
    // If address is an object, format it
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.area) parts.push(address.area);
    if (address.city) parts.push(address.city);
    
    return parts.length > 0 ? parts.join(', ') : "No address details";
  };

  // Fetch medication details and assigned facilities
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch the medication details
        if (medicationId) {
          const medicationResponse = await getMedicationById(medicationId);
          setMedication(medicationResponse.data);
          
          // Find facilities that have this medication assigned
          // We'll need to fetch facilities with this medication
          const facilitiesWithMedication = await findFacilitiesWithMedication(medicationId);
          console.log('#####API RES######: ', facilitiesWithMedication)
          setAssignedFacilities(facilitiesWithMedication?.data?.facilities || []);
        }
      } catch (error) {
        toast.error('Error fetching data');
        console.error('Fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [medicationId]);

  // Show confirmation modal before unassigning
  const showConfirmUnassign = (facilityId) => {
    setSelectedFacilityId(facilityId);
    
    Modal.confirm({
      title: 'Unassign Medication',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to unassign this medication from the selected facility?',
      okText: 'Unassign',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleUnassign(facilityId);
      },
      onCancel() {
        setSelectedFacilityId(null);
      },
    });
  };

  // Handle unassigning medication from facility
  const handleUnassign = async (facilityId) => {
    setUnassignLoading(true);
    try {

        console.log('facility ID: ', facilityId);
        console.log('MEDICATION ID: ', medicationId);
        return;
      await removeMedicationFromFacility(medicationId, facilityId);
      
      // Update the list of assigned facilities
      setAssignedFacilities(prevFacilities => 
        prevFacilities.filter(facility => facility.id !== facilityId)
      );
      
      toast.success('Medication unassigned from facility successfully');
      
      if (typeof onUnassignComplete === 'function') {
        onUnassignComplete();
      }
    } catch (error) {
      console.error('Unassign error:', error);
      toast.error(error.message || 'Error unassigning medication from facility');
    } finally {
      setUnassignLoading(false);
      setSelectedFacilityId(null);
    }
  };

  if (loading) {
    return (
      <div className="modal-wrapper">
        <div className="modal-container">
          <div className="modal-content loading-content">
            <Spin size="large" />
          </div>
        </div>
        <div className="modal-overlay" onClick={onClose} />
      </div>
    );
  }

  return (
    <div className="modal-wrapper">
      <div className="modal-container unassign-modal">
        <div className="modal-header">
          <h2>Unassign Medication from Facilities</h2>
          <ImCancelCircle 
            className="close-icon"
            onClick={onClose} 
            size={22} 
          />
        </div>

        <div className="modal-content">
          {medication && (
            <div className="medication-details">
              <div className="medication-header">
                <MedicineBoxOutlined className="medication-icon" />
                <div className="medication-title">
                  <h3>{medication.name}</h3>
                  <span className="medication-presentation">{medication.presentation}</span>
                </div>
              </div>
              <div className="medication-info">
                <div className="info-item">
                  <span className="info-label">Active Molecules:</span>
                  <span className="info-value">{medication.activeMoleculesAndDosage}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Therapeutic Class:</span>
                  <span className="info-value">{medication.therapeuticClass}</span>
                </div>
                {medication.price && (
                  <div className="info-item">
                    <span className="info-label">Price:</span>
                    <span className="info-value">${medication.price}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="assigned-facilities-section">
            <h3>Assigned Facilities</h3>
            
            {assignedFacilities.length > 0 ? (
              <div className="facility-list">
                <List
                  itemLayout="horizontal"
                  dataSource={assignedFacilities}
                  renderItem={facility => (
                    <List.Item
                      className="facility-list-item"
                      actions={[
                        <Tooltip title="Unassign from this facility">
                          <Button
                            type="danger"
                            ghost
                            icon={<DisconnectOutlined />}
                            onClick={() => showConfirmUnassign(facility.id)}
                            loading={unassignLoading && selectedFacilityId === facility.id}
                            className="unassign-button"
                          >
                            Unassign
                          </Button>
                        </Tooltip>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar 
                            icon={<EnvironmentOutlined />} 
                            className="facility-avatar"
                            style={{ 
                              backgroundColor: 
                                facility.facilityType === 'hospital' ? '#ff4d4f' : 
                                facility.facilityType === 'pharmacy' ? '#52c41a' : '#1890ff' 
                            }}
                          />
                        }
                        title={
                          <div className="facility-title">
                            {facility.facilityName}
                            <Tag color="blue">{facility.facilityType}</Tag>
                          </div>
                        }
                        description={
                          <div className="facility-description">
                            <EnvironmentOutlined /> {formatAddress(facility.address)}
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            ) : (
              <Empty 
                description="This medication is not assigned to any facilities" 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </div>

          <div className="button-container">
            <Button 
              onClick={onClose} 
              className="cancel-button"
              size="large"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
      <div className="modal-overlay" onClick={onClose} />
    </div>
  );
};

export default UnassignMedicationModal;